import { api } from "@/api";
import { MutationConfig, MutationResult } from "@/shared/types/base";
import { CajaMovimiento } from "@/shared/types/caja-movimiento";
import { Transferencia } from "@/shared/types/transferencia";
import { Venta } from "@/shared/types/venta";
import { sleep } from "@/shared/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ReactNode } from "react";
import { VentaRequest } from "@/features/inventario/ventas/api/add-venta";
import { PayVentaRequest } from "./pay-venta";

export type Pago = {
    fecha: string,
    venta: Venta,
    pago: Transferencia,
    vuelto: Transferencia,
    movimiento: CajaMovimiento
}

export interface CreateAndPayVentaRequest {
    ventaRequest: VentaRequest,
    pagoRequest: PayVentaRequest,
}

export async function createAndPayVenta(request: CreateAndPayVentaRequest): Promise<Pago> {
    await sleep(1000);
    const response = await api.post("caja_actual/pay", request, { headers: { "X-Venta-Token": request.ventaRequest.token } });
    return response.data;
}

export function useCreateAndPayVentaMutation(mutationConfig: MutationConfig<Pago, CreateAndPayVentaRequest> = {}) {
    const queryClient = useQueryClient();
    return useMutation({
        ...mutationConfig,
        mutationFn: createAndPayVenta,
        onSuccess: (...args) => {
            mutationConfig.onSuccess && mutationConfig.onSuccess(...args);
            queryClient.invalidateQueries({ queryKey: ["ventas", "unpaid"] });
        },
    })
}

export interface CreateAndPayVentaMutationProps {
    mutationConfig?: MutationConfig<Pago, CreateAndPayVentaRequest>
    children?: (mutationResult: MutationResult<Pago, CreateAndPayVentaRequest>) => ReactNode
}

export function CreateAndPayVentaMutation({
    mutationConfig,
    children
}: CreateAndPayVentaMutationProps) {
    const mutationResult = useCreateAndPayVentaMutation(mutationConfig);
    return children && children(mutationResult);
}